/**
 * Hooks del dominio Notificaciones (campana de pedidos de láminas, TanStack Query).
 *
 * Datos de servidor -> TanStack Query (`docs/frontend_conventions.md`, Datos): no
 * `useState`+`useEffect`+`fetch`. La red la gobierna `lib/api` (cookie httpOnly +
 * `X-Sede-Id`). La campana se **sondea** en segundo plano (réplica del polling del
 * SSR): la query usa `refetchInterval` y sigue refrescando aunque la ventana no esté
 * en foco (las novedades deben llegar mientras se trabaja en otra pestaña).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { getActiveSedeId } from "@/lib/sede";

import { fetchNotifications, markNotificationsSeen } from "../api";
import { NOTIFICATIONS_POLL_MS } from "../constants";
import type { PedidoNotificationsResponse } from "../types";
import { notificationsQueryKey } from "./queryKeys";

/**
 * Notificaciones no vistas de la sede activa, sondeadas cada `NOTIFICATIONS_POLL_MS`.
 *
 * `enabled` (por defecto `true`) permite a quien la monta NO sondear si el usuario
 * carece del permiso de la campana (`view_pedidos_laminas`): así no se pide un dato
 * que el actor no debe ver (mismo patrón que `useActivitySummary`). La key incluye la
 * sede para recargar al cambiarla.
 */
export function useNotifications(enabled = true) {
  const sedeId = getActiveSedeId();
  return useQuery<PedidoNotificationsResponse>({
    queryKey: notificationsQueryKey(sedeId),
    queryFn: fetchNotifications,
    enabled,
    // Sondeo de fondo (como el SSR): refresca aunque la pestaña no tenga foco.
    refetchInterval: NOTIFICATIONS_POLL_MS,
    refetchIntervalInBackground: true,
  });
}

/**
 * Marca como vistas las notificaciones de la sede activa (al abrir la campana) e
 * invalida la query para que el badge se ponga a cero de inmediato.
 */
export function useMarkNotificationsSeen() {
  const queryClient = useQueryClient();
  const sedeId = getActiveSedeId();
  return useMutation<void, Error, void>({
    mutationFn: markNotificationsSeen,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: notificationsQueryKey(sedeId),
      });
    },
  });
}
